import React, { useRef, useState, useMemo } from 'react';
import { StatusBar, apptStatusColor } from './StatusBar';
import { NotesRow } from './NotesRow';
import { CheckCallDialog, CheckCallHistoryDialog, CONDITION_COLORS } from '../check-calls/CheckCallDialog';
import { DateTimeInput, DateInput, TimeInput } from '../DateTimeInput';
import { SearchableSelect } from '../ui/searchable-select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { documentAPI } from '../../api';
import { toast } from 'sonner';

const APPT_STATUSES = ['Needs Scheduling', 'Requested', 'Granted', 'Updated TMS', 'Change'];

// Reusable panel for pickup or delivery stops with switcher, add-stop, scheduling-aware buttons
export const StopPanel = ({ title, stops, facilities, loadId, load, onUpdateStop, onAddStop, onRemoveStop, onMoveStop, onOpenNotes, onSendInfo, onSaveNote, onCheckCallSaved, currentIdx, onSwitch, lastNote }) => {
  const fileInputRef = useRef(null);
  const [screenshotOpen, setScreenshotOpen] = useState(false);
  const [portalCredsOpen, setPortalCredsOpen] = useState(false);
  const [checkCallOpen, setCheckCallOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const facilityOptions = useMemo(() => facilities.map(f => ({ value: f.id, label: f.name })), [facilities]);

  const entry = stops[currentIdx];
  const stopType = title === 'Pickup' ? 'pickup' : 'delivery';

  const TitleControls = () => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      <button
        onClick={() => onAddStop(stopType)}
        title={`Add ${title.toLowerCase()}`}
        style={{ width: 20, height: 20, border: '1px solid rgba(255,255,255,0.6)', borderRadius: 3, background: 'rgba(255,255,255,0.25)', color: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
        data-testid={`lw-add-stop-${stopType}`}
      >+</button>
      {stops.length > 1 && (
        <>
          <button
            onClick={() => onSwitch(Math.max(0, currentIdx - 1))}
            disabled={currentIdx === 0}
            style={{ width: 18, height: 18, border: 'none', background: 'transparent', color: 'inherit', cursor: currentIdx === 0 ? 'default' : 'pointer', opacity: currentIdx === 0 ? 0.4 : 1, fontSize: 13, fontWeight: 700 }}
            data-testid={`lw-stop-prev-${stopType}`}
          >‹</button>
          <button
            onClick={() => onSwitch(Math.min(stops.length - 1, currentIdx + 1))}
            disabled={currentIdx === stops.length - 1}
            style={{ width: 18, height: 18, border: 'none', background: 'transparent', color: 'inherit', cursor: currentIdx === stops.length - 1 ? 'default' : 'pointer', opacity: currentIdx === stops.length - 1 ? 0.4 : 1, fontSize: 13, fontWeight: 700 }}
            data-testid={`lw-stop-next-${stopType}`}
          >›</button>
        </>
      )}
      <span>{title}{stops.length > 1 ? ` (${currentIdx + 1}/${stops.length})` : ''}</span>
      {entry && stops.length > 1 && onMoveStop && (
        <span style={{ display: 'inline-flex', gap: 1, marginLeft: 2 }} title="Reorder this stop (all data stays with it)">
          <button
            onClick={() => { onMoveStop(entry.index, -1); onSwitch(currentIdx - 1); }}
            disabled={currentIdx === 0}
            style={{ width: 16, height: 18, border: 'none', background: 'transparent', color: 'inherit', cursor: currentIdx === 0 ? 'default' : 'pointer', opacity: currentIdx === 0 ? 0.35 : 1, fontSize: 10, lineHeight: 1, padding: 0 }}
            title="Move up (becomes the previous stop)"
            data-testid={`lw-stop-move-up-${stopType}`}
          >▲</button>
          <button
            onClick={() => { onMoveStop(entry.index, 1); onSwitch(currentIdx + 1); }}
            disabled={currentIdx === stops.length - 1}
            style={{ width: 16, height: 18, border: 'none', background: 'transparent', color: 'inherit', cursor: currentIdx === stops.length - 1 ? 'default' : 'pointer', opacity: currentIdx === stops.length - 1 ? 0.35 : 1, fontSize: 10, lineHeight: 1, padding: 0 }}
            title="Move down (becomes the next stop)"
            data-testid={`lw-stop-move-down-${stopType}`}
          >▼</button>
        </span>
      )}
      {entry && stops.length > 1 && (
        <button
          onClick={() => {
            if (window.confirm(`Remove this ${title.toLowerCase()} stop?`)) onRemoveStop(entry.index);
          }}
          title={`Remove this ${title.toLowerCase()}`}
          style={{ width: 18, height: 18, border: 'none', background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: 0.75 }}
          data-testid={`lw-remove-stop-${stopType}`}
        >✕</button>
      )}
    </span>
  );

  if (!entry) {
    return (
      <div className="lw-panel" data-testid={`lw-panel-${title.toLowerCase()}`}>
        <StatusBar left={<TitleControls />} right="—" color="neutral" />
        <p style={{ fontSize: 13, color: 'var(--lw-muted)' }}>No {title.toLowerCase()} stops. Use + to add one.</p>
      </div>
    );
  }

  const { stop, index } = entry;
  const isPickup = stop.type === 'pickup';
  const facility = facilities.find(f => f.id === stop.facility_id);
  const color = apptStatusColor(stop.appointment_status);
  const refValue = isPickup ? (stop.references?.[0] || '') : (stop.po_number || '');
  const screenshots = stop.appt_screenshots || [];
  const lastCheck = stop.last_check_call || null;
  const schedulingMethod = (facility?.scheduling_method || 'FCFS').toLowerCase();

  const openPortal = () => {
    window.open(facility.portal_url.startsWith('http') ? facility.portal_url : `https://${facility.portal_url}`, '_blank');
    if (facility.portal_username || facility.portal_password) setPortalCredsOpen(true);
  };

  const buildApptEmailSubject = () => {
    const missing = [];
    const ref = isPickup ? (stop.references?.[0] || '').trim() : (stop.po_number || '').trim();
    if (!ref) missing.push(isPickup ? 'PU Ref #' : 'PO #');
    const [datePart, timePart] = (stop.appointment_datetime || '').split('T');
    let dateStr = '';
    let timeStr = '';
    if (datePart && /^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
      const [y, m, d] = datePart.split('-');
      dateStr = `${m}/${d}/${y}`;
    } else missing.push('Appointment Date');
    if (timePart && /^\d{2}:\d{2}/.test(timePart)) timeStr = timePart.slice(0, 5);
    else missing.push('Appointment Time');
    if (missing.length) return { missing };
    const subject = `Requesting appointment for ${stop.type} PO ${ref} on ${dateStr} @ ${timeStr}`;
    const body = [
      'Hello,',
      '',
      `We would like to request a ${stop.type} appointment:`,
      '',
      `${isPickup ? 'PU Ref #' : 'PO #'}: ${ref}`,
      `Requested date/time: ${dateStr} @ ${timeStr}`,
      '',
      'Please confirm the appointment at your earliest convenience.',
    ].join('\n');
    return { subject, body };
  };

  const handleScheduleAction = () => {
    if (!facility) { toast.error('Select a facility first'); return; }
    if (schedulingMethod === 'portal' && facility.portal_url) {
      openPortal();
    } else if (schedulingMethod === 'email' && facility.scheduling_email) {
      const { subject, body, missing } = buildApptEmailSubject();
      if (missing) {
        toast.error(`Cannot request appointment — missing: ${missing.join(', ')}`);
        return;
      }
      const qs = [
        facility.scheduling_email_cc ? `cc=${encodeURIComponent(facility.scheduling_email_cc)}` : null,
        `subject=${encodeURIComponent(subject)}`,
        `body=${encodeURIComponent(body)}`,
      ].filter(Boolean).join('&');
      window.location.href = `mailto:${facility.scheduling_email}?${qs}`;
      if ((stop.appointment_status || 'Needs Scheduling') !== 'Requested') {
        onUpdateStop(index, 'appointment_status', 'Requested');
        toast.success('Appointment status set to Requested — remember to Save');
      }
    } else if (schedulingMethod === 'phone' && facility.scheduling_phone) {
      window.location.href = `tel:${facility.scheduling_phone.replace(/[^\d+]/g, '')}`;
    } else {
      toast.info(`Scheduling method: ${facility.scheduling_method || 'FCFS'} — no portal/email/phone configured on facility profile`);
    }
  };

  const scheduleBtnLabel = schedulingMethod === 'portal' ? 'Portal' : schedulingMethod === 'email' ? 'Send Email' : schedulingMethod === 'phone' ? 'Call' : 'FCFS';

  const handleScreenshotUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const response = await documentAPI.upload(
        file, 'load', loadId,
        `appt_screenshot_stop_${index + 1}`,
        `Appointment Screenshot for Stop ${index + 1}`
      );
      const fileUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.file_url}`;
      onUpdateStop(index, 'appt_screenshots', [...screenshots, fileUrl]);
      toast.success('Document added — remember to Save');
    } catch {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const copyToClipboard = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch (e) {
      toast.error('Copy failed');
    }
  };

  const removeScreenshot = (i) => {
    const next = [...screenshots];
    next.splice(i, 1);
    onUpdateStop(index, 'appt_screenshots', next);
  };

  return (
    <div className="lw-panel" data-testid={`lw-panel-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <StatusBar
        left={<TitleControls />}
        right={
          <select
            className="lw-input"
            style={{ height: 24, maxWidth: 150, fontSize: 12, fontWeight: 700 }}
            value={stop.appointment_status || 'Needs Scheduling'}
            onChange={(e) => onUpdateStop(index, 'appointment_status', e.target.value)}
            onClick={(e) => e.stopPropagation()}
            data-testid={`lw-appt-status-${index}`}
          >
            {APPT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        }
        color={color}
        testId={`lw-statusbar-${title.toLowerCase()}`}
      />

      <div className="lw-row">
        <span
          className="lw-link"
          style={{ width: 80 }}
          onClick={() => stop.facility_id && window.open(`/facilities/${stop.facility_id}`, '_blank')}
        >Facility</span>
        <SearchableSelect
          options={facilityOptions}
          value={stop.facility_id || ''}
          onValueChange={(val) => {
            const f = facilities.find(x => x.id === val);
            onUpdateStop(index, 'facility_id', val);
            if (f) {
              onUpdateStop(index, 'facility_name', f.name || '');
              onUpdateStop(index, 'address', f.address || '');
              onUpdateStop(index, 'city', f.city || '');
              onUpdateStop(index, 'state', f.state || '');
              onUpdateStop(index, 'zip_code', f.zip_code || '');
            }
          }}
          placeholder="Select facility..."
          className="h-7 text-sm flex-1"
          data-testid={`lw-facility-select-${index}`} data-field={`stops.${index}.facility_id`}
        />
      </div>
      <div className="lw-row">
        <span className="lw-link" style={{ width: 80 }}>Address</span>
        <input className="lw-input" readOnly value={stop.address || facility?.address || ''} data-testid={`lw-facility-address-${index}`} />
      </div>
      <div className="lw-row">
        <input className="lw-input" readOnly value={[stop.city || facility?.city, stop.state || facility?.state, stop.zip_code || facility?.zip_code].filter(Boolean).join(', ')} data-testid={`lw-facility-citystate-${index}`} />
      </div>

      <div className="lw-sep" />

      <div className="lw-row">
        <span className="lw-label w95">Appointment</span>
        <DateTimeInput
          value={stop.appointment_datetime || ''}
          onChange={(e) => onUpdateStop(index, 'appointment_datetime', e.target.value)}
          className="lw-input"
          placeholder="MM/DD HH:MM"
          data-testid={`lw-appointment_datetime-${index}`} data-field={`stops.${index}.appointment_datetime`}
        />
        <span className="lw-label" style={{ marginLeft: 4, marginRight: 4, width: 'auto' }}>to</span>
        <TimeInput
          value={stop.appointment_end_time || ''}
          onChange={(e) => onUpdateStop(index, 'appointment_end_time', e.target.value)}
          className="lw-input"
          style={{ maxWidth: 72 }}
          placeholder="HH:MM"
          title="Appointment window end time"
          data-testid={`lw-appointment-end-${index}`}
        />
      </div>
      {[
        { label: 'Arrival', field: 'actual_arrival' },
        { label: 'Departure', field: 'actual_departure' },
      ].map(({ label, field }) => (
        <div className="lw-row" key={field}>
          <span className="lw-label w95">{label}</span>
          <DateTimeInput
            value={stop[field] || ''}
            onChange={(e) => onUpdateStop(index, field, e.target.value)}
            className="lw-input"
            placeholder="MM/DD HH:MM"
            data-testid={`lw-${field}-${index}`}
          />
        </div>
      ))}

      <div className="lw-row">
        <span className="lw-link" style={{ width: 95 }}>{isPickup ? 'PU Ref #' : 'PO #'}</span>
        <input
          className="lw-input"
          value={refValue}
          onChange={(e) => {
            if (isPickup) onUpdateStop(index, 'references', [e.target.value]);
            else onUpdateStop(index, 'po_number', e.target.value);
          }}
          data-testid={`lw-ref-${index}`} data-field={`stops.${index}.references`}
        />
        {!isPickup && (
          <>
            <span className="lw-link" style={{ width: 'auto', marginLeft: 8, marginRight: 6 }}>DEL #</span>
            <input
              className="lw-input"
              value={stop.delivery_number || ''}
              onChange={(e) => onUpdateStop(index, 'delivery_number', e.target.value)}
              placeholder="Delivery #"
              data-testid={`lw-del-${index}`} data-field={`stops.${index}.delivery_number`}
            />
          </>
        )}
      </div>

      <NotesRow lastNote={lastNote} onOpenNotes={() => onOpenNotes(index)} onSaveNote={(text) => onSaveNote(text, index)} testId={`lw-notes-${index}`} />

      {lastCheck && (
        <div
          className="lw-row"
          style={{ gap: 6, cursor: 'pointer' }}
          onClick={() => setHistoryOpen(true)}
          title="Click to view check call history"
          data-testid={`lw-check-call-status-${index}`}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#0c4a25', background: '#b8e6c9', borderRadius: 4, padding: '2px 8px' }}>
            ✓ Checked
          </span>
          {lastCheck.condition && (
            <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: CONDITION_COLORS[lastCheck.condition]?.border, borderRadius: 4, padding: '2px 6px' }}>{lastCheck.condition}</span>
          )}
          <span style={{ fontSize: 12, color: 'var(--lw-muted)' }}>
            {lastCheck.checked_at_central_display} • {lastCheck.checked_by_name}
            {lastCheck.driver_status ? ` • ${lastCheck.driver_status}` : ''}
            {lastCheck.eta ? ` • ETA ${lastCheck.eta}` : ''}
            {stop.check_call_count > 1 ? ` • ${stop.check_call_count} calls` : ''}
          </span>
        </div>
      )}
      <CheckCallHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} loadId={loadId} stopIndex={index} title={`${load?.load_number || ''} · ${stop.facility_name || facility?.name || title}`} />

      <div className="lw-row" style={{ gap: 5, width: '100%' }}>
        <span className="lw-label" style={{ fontSize: 12.5 }}>Drop Off</span>
        <input
          type="checkbox"
          className="lw-checkbox"
          style={{ width: 15, height: 15 }}
          checked={!!stop.drop_off}
          onChange={(e) => onUpdateStop(index, 'drop_off', e.target.checked)}
          data-testid={`lw-stop-drop-off-${index}`}
        />
        {stop.drop_off && (
          <>
            <span className="lw-label" style={{ fontSize: 12.5, marginLeft: 4 }}>Dropped</span>
            <DateInput
              value={stop.trailer_dropped_date || ''}
              onChange={(e) => onUpdateStop(index, 'trailer_dropped_date', e.target.value)}
              className="lw-input"
              style={{ flex: 1, minWidth: 0, fontSize: 12.5, height: 26, padding: '2px 6px' }}
              data-testid={`lw-stop-dropped-${index}`}
            />
            <span className="lw-label" style={{ fontSize: 12.5, marginLeft: 4 }}>Ready PU</span>
            <DateInput
              value={stop.trailer_ready_for_pickup_date || ''}
              onChange={(e) => onUpdateStop(index, 'trailer_ready_for_pickup_date', e.target.value)}
              className="lw-input"
              style={{ flex: 1, minWidth: 0, fontSize: 12.5, height: 26, padding: '2px 6px' }}
              data-testid={`lw-stop-ready-${index}`}
            />
            <span className="lw-label" style={{ fontSize: 12.5, marginLeft: 4 }}>Pulled</span>
            <DateInput
              value={stop.trailer_pulled_date || ''}
              onChange={(e) => onUpdateStop(index, 'trailer_pulled_date', e.target.value)}
              className="lw-input"
              style={{ flex: 1, minWidth: 0, fontSize: 12.5, height: 26, padding: '2px 6px' }}
              data-testid={`lw-stop-pulled-${index}`}
            />
          </>
        )}
      </div>

      <div className="lw-btnrow">
        <button className="lw-btn" onClick={handleScheduleAction} title={`Scheduling method: ${facility?.scheduling_method || 'FCFS'}`} data-testid={`lw-btn-schedule-${index}`}>
          {scheduleBtnLabel}
        </button>
        <button className="lw-btn" onClick={() => facility && window.open(`/facilities/${facility.id}`, '_blank')} title="Open facility profile in a new tab" data-testid={`lw-btn-facility-${index}`}>Facility</button>
        {schedulingMethod !== 'portal' && facility?.portal_url && (
          <button className="lw-btn" onClick={openPortal} title="Open facility portal" data-testid={`lw-btn-portal-${index}`}>Portal</button>
        )}
        {isPickup && (
          <button className="lw-btn" onClick={() => onSendInfo(stop)} title="Email carrier info to facility" data-testid={`lw-btn-send-info-${index}`}>Send Info</button>
        )}
        <button className="lw-btn" onClick={() => setScreenshotOpen(true)} data-testid={`lw-btn-documents-${index}`}>
          Documents{screenshots.length > 0 ? ` (${screenshots.length})` : ''}
        </button>
        <button
          className="lw-btn"
          onClick={() => setCheckCallOpen(true)}
          disabled={!stop.facility_id}
          title={stop.facility_id ? 'Record a check call for this stop' : 'Select a facility first'}
          style={lastCheck ? { borderColor: '#0c4a25', color: '#0c4a25' } : {}}
          data-testid={`lw-btn-check-call-${index}`}
        >
          {lastCheck ? '✓ Check Call' : 'Check Call'}
        </button>
      </div>

      <CheckCallDialog
        open={checkCallOpen}
        onOpenChange={setCheckCallOpen}
        item={{
          load_id: loadId,
          load_number: load?.load_number,
          stop_index: index,
          stop_type: stop.type,
          facility_name: stop.facility_name || facility?.name,
          city: stop.city || facility?.city,
          state: stop.state || facility?.state,
          appointment_datetime: stop.appointment_datetime,
          load_status: load?.status,
        }}
        onSaved={(rec) => onCheckCallSaved && onCheckCallSaved(index, rec)}
      />

      {/* Portal credentials modal */}
      <Dialog open={portalCredsOpen} onOpenChange={setPortalCredsOpen}>
        <DialogContent className="max-w-sm" data-testid={`lw-portal-creds-modal-${index}`}>
          <DialogHeader>
            <DialogTitle>Portal Login — {facility?.name}</DialogTitle>
            <DialogDescription className="sr-only">Facility portal credentials</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Username', value: facility?.portal_username },
              { label: 'Password', value: facility?.portal_password },
            ].filter(c => c.value).map(({ label, value }) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="lw-label" style={{ width: 75 }}>{label}</span>
                <input className="lw-input" readOnly value={value} style={{ flex: 1 }} data-testid={`lw-portal-${label.toLowerCase()}-${index}`} />
                <button className="lw-btn" style={{ height: 26, padding: '0 10px' }} onClick={() => copyToClipboard(label, value)} data-testid={`lw-portal-copy-${label.toLowerCase()}-${index}`}>Copy</button>
              </div>
            ))}
            {facility?.portal_instructions && (
              <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', whiteSpace: 'pre-wrap' }}>{facility.portal_instructions}</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents modal */}
      <Dialog open={screenshotOpen} onOpenChange={setScreenshotOpen}>
        <DialogContent className="max-w-md" data-testid={`lw-documents-modal-${index}`}>
          <DialogHeader>
            <DialogTitle>Documents — {title}</DialogTitle>
            <DialogDescription className="sr-only">Upload documents or screenshots of the appointment confirmation</DialogDescription>
          </DialogHeader>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {screenshots.length === 0 && <p style={{ fontSize: 13, color: 'var(--lw-muted)' }}>No documents yet.</p>}
            {screenshots.map((url, i) => (
              <div key={url} style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--lw-border)', borderRadius: 4, padding: 6 }}>
                <a href={url} target="_blank" rel="noreferrer" className="lw-link" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  Document {i + 1}
                </a>
                <button className="lw-btn" style={{ height: 24, padding: '0 8px', color: 'var(--lw-red)' }} onClick={() => removeScreenshot(i)} data-testid={`lw-document-remove-${i}`}>✕</button>
              </div>
            ))}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => { handleScreenshotUpload(e.target.files?.[0]); e.target.value = ''; }}
              data-testid={`lw-document-file-input-${index}`}
            />
            <button
              className="lw-btn primary"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid={`lw-document-upload-btn-${index}`}
            >
              {uploading ? 'Uploading...' : '+ Add Document'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
